
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import SystemAlert from '../../common/SystemAlert.js';
import AgentInstructions from '../agent/AgentInstructions';
import InstructionBuilder from './InstructionBuilder.js';
import InstructionChecker, { INSTRUCTION_TEMPLATES } from '../common/InstructionChecker';
import PromptEngineerFlow from './promptEngineer/PromptEngineerFlow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import { getInstructionLifecycleChipProps } from './instructionLifecycleChip';
import axios from 'axios';
import {
  materializePromptEngineerGenerationSession,
  publishPromptEngineerDraft
} from '../../services/promptEngineerService';
export default function InstructionsInfo() {
  const navigate = useNavigate();
  const { instructionId } = useParams();
  const [alert, setAlert] = useState(null);
  const [selectedInstructionId, setSelectedInstructionId] = useState(instructionId ? parseInt(instructionId) : null);
  const [saving, setSaving] = useState(false);
  const [creationMode, setCreationMode] = useState(null); // 'template' | 'wizard' | 'paste' | 'promptEngineer'
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [expandedTemplateKey, setExpandedTemplateKey] = useState(null);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [editMode, setEditMode] = useState('standard');
  const [promptEngineerHasUnsaved, setPromptEngineerHasUnsaved] = useState(false);
  const [confirmLeavePromptEngineerOpen, setConfirmLeavePromptEngineerOpen] = useState(false);
  const [confirmReopenPromptEngineerOpen, setConfirmReopenPromptEngineerOpen] = useState(false);
  const [pendingPromptEngineerDiscardAction, setPendingPromptEngineerDiscardAction] = useState(null);
  const [promptEngineerSaveForLaterAction, setPromptEngineerSaveForLaterAction] = useState(null);
  const autoOpenedDraftRef = useRef(null);
  const handlePromptEngineerSaveForLaterRequestChange = React.useCallback((action) => {
    setPromptEngineerSaveForLaterAction(() => action || null);
  }, []);

  const navigateToListWithAlert = React.useCallback((nextAlert) => {
    navigate('/InstructionsList', {
      state: {
        alert: nextAlert
      }
    });
  }, [navigate]);

  const buildPromptEngineerResumeSnapshot = React.useCallback((instruction) => {
    if (!instruction || instruction.sourceType !== 'ai_prompt_engineer') {
      return null;
    }

    const workspaceSnapshot = instruction?.promptEngineerMeta?.workspaceSnapshot;
    if (!workspaceSnapshot || typeof workspaceSnapshot !== 'object') {
      return null;
    }

    const stage = typeof workspaceSnapshot.stage === 'string' ? workspaceSnapshot.stage : 'interview';
    const currentPhase = Number.isInteger(workspaceSnapshot.currentPhase)
      ? workspaceSnapshot.currentPhase
      : 0;
    const selectedDraftVersion = instruction?.promptEngineerMeta?.selectedDraftVersion;

    return {
      stage,
      currentPhase,
      config: instruction?.promptEngineerConfig || null,
      instructionName: instruction?.name || '',
      draftText: typeof workspaceSnapshot.draftText === 'string'
        ? workspaceSnapshot.draftText
        : (instruction?.content || ''),
      optimizedText: typeof workspaceSnapshot.optimizedText === 'string'
        ? workspaceSnapshot.optimizedText
        : '',
      generationSessionKey: instruction?.promptEngineerMeta?.generationSessionKey || '',
      activeDraftVersion: selectedDraftVersion === 'optimized'
        ? 'optimized'
        : (workspaceSnapshot.activeDraftVersion === 'optimized' ? 'optimized' : 'original')
    };
  }, []);

  useEffect(() => {
    const loadInstruction = async () => {
      if (!selectedInstructionId) {
        setSelectedInstruction(null);
        return;
      }
      try {
        const response = await axios.post(`/instructions/get?id=${selectedInstructionId}`);
        setSelectedInstruction(response.data || null);
      } catch (err) {
        setSelectedInstruction(null);
      }
    };

    loadInstruction();
  }, [selectedInstructionId]);

  useEffect(() => {
    if (!selectedInstructionId || !selectedInstruction) {
      autoOpenedDraftRef.current = null;
      return;
    }

    const lifecycleState = (selectedInstruction?.lifecycleState || '').toUpperCase();
    const isArchitectDraft = selectedInstruction?.sourceType === 'ai_prompt_engineer'
      && lifecycleState === 'DRAFT'
      && Boolean(selectedInstruction?.promptEngineerConfig);

    if (!isArchitectDraft) {
      autoOpenedDraftRef.current = null;
      return;
    }

    if (autoOpenedDraftRef.current === selectedInstructionId) {
      return;
    }

    autoOpenedDraftRef.current = selectedInstructionId;
    setEditMode('promptEngineer');
  }, [selectedInstructionId, selectedInstruction]);

  const isPromptEngineerSessionActive = React.useCallback(() => {
    const isCreatePromptEngineer = !instructionId && creationMode === 'promptEngineer';
    const isEditPromptEngineer = Boolean(instructionId) && editMode === 'promptEngineer';
    return isCreatePromptEngineer || isEditPromptEngineer;
  }, [instructionId, creationMode, editMode]);

  const requestLeavePromptEngineer = React.useCallback((leaveAction) => {
    if (!promptEngineerHasUnsaved || !isPromptEngineerSessionActive()) {
      leaveAction?.();
      return;
    }
    setPendingPromptEngineerDiscardAction(() => leaveAction || null);
    setConfirmLeavePromptEngineerOpen(true);
  }, [promptEngineerHasUnsaved, isPromptEngineerSessionActive]);

  // Handler for Save/Cancel actions
  const handleCancel = () => {
    requestLeavePromptEngineer(() => navigate('/InstructionsList'));
  };

  // Handler for saving instruction from InstructionBuilder
  const handleSaveInstruction = async (instruction) => {
    try {
      setSaving(true);
      // Check for duplicate name before sending to backend
      const allInstructionsResponse = await axios.post('/instructions/getAll');
      const nameExists = allInstructionsResponse.data.some(instr =>
        instr.name?.toLowerCase().trim() === instruction.name.toLowerCase().trim()
      );

      if (nameExists) {
        // Create a unique name by appending a counter: "Name (2)", "Name (3)", etc.
        const existingNames = allInstructionsResponse.data.map(instr => (instr.name || '').toLowerCase().trim());
        const base = (instruction.name || '').trim();
        let candidate = base;
        let counter = 2;
        while (existingNames.includes(candidate.toLowerCase())) {
          candidate = `${base} (${counter})`;
          counter += 1;
        }
        // Inform the user that we've adjusted the name to avoid duplicate errors
        setAlert({ severity: 'info', message: `An instruction with that name exists — saving as "${candidate}".` });
        // Use the unique candidate name for saving
        instruction = { ...instruction, name: candidate };
      }

      // Remove id if present before sending to backend
      const { id, ...newInstruction } = instruction;
      const response = await axios.post('/instructions/create', newInstruction);

      setAlert({ severity: 'success', message: 'Instruction created successfully!' });
      navigateToListWithAlert({ severity: 'success', message: 'Instruction created successfully!' });

      return response.data;
    } catch (err) {
      console.error('Error saving instruction:', err);
      if (err.response?.data?.message) {
        setAlert({ severity: 'error', message: err.response.data.message });
      } else if (err.message && err.message !== 'Duplicate name') {
        setAlert({ severity: 'error', message: err.message });
      }
      setSaving(false);
      throw err;
    }
  };

  const templateEntries = Object.entries(INSTRUCTION_TEMPLATES);

  const formatTemplateTitle = (key) => {
    if (!key) return '';
    const withSpaces = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[-_]/g, ' ')
      .trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const handleTemplateSelect = (key) => {
    setSelectedTemplateKey(key);
    setTemplateContent(INSTRUCTION_TEMPLATES[key] || '');
    setTemplateName(formatTemplateTitle(key));
  };

  const resetTemplateSelection = () => {
    setSelectedTemplateKey(null);
    setTemplateContent('');
    setTemplateName('');
    setExpandedTemplateKey(null);
  };

  const handleSaveFromTemplate = async () => {
    if (!templateName?.trim() || !templateContent?.trim()) return;
    await handleSaveInstruction({ name: templateName.trim(), content: templateContent });
  };

  const handleSaveManual = async () => {
    if (!manualName?.trim() || !manualContent?.trim()) return;
    await handleSaveInstruction({ name: manualName.trim(), content: manualContent });
  };

  const handleSavePromptEngineerEdit = async (instructionPayload) => {
    try {
      setSaving(true);

      const selectedLifecycleState = (selectedInstruction?.lifecycleState || '').toUpperCase();
      if (selectedLifecycleState === 'DRAFT') {
        const sourceInstructionId = selectedInstruction?.promptEngineerMeta?.sourceInstructionId || null;
        const generationSessionKey = instructionPayload?.promptEngineerMeta?.generationSessionKey;

        let draftInstructionId = selectedInstructionId;
        if (generationSessionKey) {
          const materializedDraft = await materializePromptEngineerGenerationSession(generationSessionKey, {
            instructionId: selectedInstructionId,
            sourceInstructionId,
            saveIntent: 'draft_save',
            lifecycleState: 'DRAFT',
            draftRole: sourceInstructionId ? 'revision_draft' : 'standalone_draft'
          });
          draftInstructionId = materializedDraft?.instruction?.id || selectedInstructionId;
        } else {
          const persistedDraft = await axios.post('/instructions/update', {
            ...instructionPayload,
            id: selectedInstructionId,
            lifecycleState: 'DRAFT',
            lifecycleReason: 'Saved for later'
          });
          draftInstructionId = persistedDraft?.data?.id || selectedInstructionId;
        }

        const published = await publishPromptEngineerDraft(draftInstructionId);
        setSelectedInstruction(published.instruction || null);
        setPromptEngineerHasUnsaved(false);

        if (published.cleanupRequired) {
          navigateToListWithAlert({ severity: 'warning', message: published.cleanupWarning || 'Instruction published successfully. Draft cleanup is still pending.' });
        } else {
          navigateToListWithAlert({ severity: 'success', message: 'Instruction published successfully!' });
        }

        return published.instruction;
      }

      const response = await axios.post('/instructions/update', {
        ...instructionPayload,
        id: selectedInstructionId
      });
      setSelectedInstruction(response.data || null);
      navigateToListWithAlert({ severity: 'success', message: 'Instruction updated successfully!' });
      setPromptEngineerHasUnsaved(false);
      return response.data;
    } catch (err) {
      console.error('Error updating instruction from prompt engineer:', err);
      if (err.response?.data?.message) {
        setAlert({ severity: 'error', message: err.response.data.message });
      } else if (err.message) {
        setAlert({ severity: 'error', message: err.message });
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const buildPromptEngineerMeta = (instructionPayload, overrides = {}) => ({
    ...(instructionPayload?.promptEngineerMeta || {}),
    ...overrides
  });

  const findExistingRevisionDraft = async (sourceInstructionId) => {
    if (!sourceInstructionId) {
      return null;
    }

    const response = await axios.post('/instructions/getAll');
    const allInstructions = response?.data || [];
    return allInstructions.find((instruction) => {
      const lifecycleState = (instruction?.lifecycleState || '').toUpperCase();
      const meta = instruction?.promptEngineerMeta || {};
      return lifecycleState === 'DRAFT'
        && instruction?.sourceType === 'ai_prompt_engineer'
        && meta?.draftRole === 'revision_draft'
        && Number(meta?.sourceInstructionId) === Number(sourceInstructionId);
    }) || null;
  };

  const persistPromptEngineerDraft = async (instructionPayload, options = {}) => {
    const {
      instructionId: draftInstructionId = null,
      sourceInstructionId = null,
      draftRole = 'standalone_draft'
    } = options;

    const generationSessionKey = instructionPayload?.promptEngineerMeta?.generationSessionKey;
    if (generationSessionKey) {
      const materialized = await materializePromptEngineerGenerationSession(generationSessionKey, {
        instructionId: draftInstructionId,
        sourceInstructionId,
        saveIntent: 'draft_save',
        lifecycleState: 'DRAFT',
        draftRole
      });
      return materialized?.instruction || null;
    }

    const payload = {
      ...instructionPayload,
      id: draftInstructionId || undefined,
      lifecycleState: 'DRAFT',
      lifecycleReason: 'Saved for later',
      promptEngineerMeta: buildPromptEngineerMeta(instructionPayload, {
        draftRole,
        sourceInstructionId: sourceInstructionId || undefined
      })
    };

    if (draftInstructionId) {
      const result = await axios.post('/instructions/update', payload);
      return result?.data || null;
    }

    const { id, ...createPayload } = payload;
    const result = await axios.post('/instructions/create', createPayload);
    return result?.data || null;
  };

  const handleSavePromptEngineerDraft = async (instructionPayload, { leaveAction = null } = {}) => {
    try {
      setSaving(true);

      const selectedLifecycleState = (selectedInstruction?.lifecycleState || '').toUpperCase();
      if (instructionId && selectedLifecycleState === 'READY') {
        const existingRevisionDraft = await findExistingRevisionDraft(selectedInstructionId);
        await persistPromptEngineerDraft(instructionPayload, {
          instructionId: existingRevisionDraft?.id || null,
          sourceInstructionId: selectedInstructionId,
          draftRole: 'revision_draft'
        });

        if (leaveAction) {
          leaveAction();
        } else {
          navigateToListWithAlert({
            severity: 'success',
            message: 'Draft saved for later. Existing published instructions remain unchanged until you publish this revision.'
          });
        }
        setPromptEngineerHasUnsaved(false);
        setEditMode('standard');
        return;
      }

      const savedDraft = await persistPromptEngineerDraft(instructionPayload, {
        instructionId: selectedLifecycleState === 'DRAFT' ? selectedInstructionId : null,
        sourceInstructionId: selectedLifecycleState === 'DRAFT'
          ? (selectedInstruction?.promptEngineerMeta?.sourceInstructionId || null)
          : null,
        draftRole: selectedLifecycleState === 'DRAFT' && selectedInstruction?.promptEngineerMeta?.sourceInstructionId
          ? 'revision_draft'
          : 'standalone_draft'
      });

      if (savedDraft?.id) {
        setSelectedInstructionId(savedDraft.id);
        setSelectedInstruction(savedDraft);
      }

      if (leaveAction) {
        leaveAction();
      } else {
        navigateToListWithAlert({ severity: 'success', message: 'Draft saved for later. You can continue your interview from Instructions.' });
      }
      setPromptEngineerHasUnsaved(false);
    } catch (err) {
      console.error('Error saving prompt engineer draft:', err);
      if (err.response?.data?.message) {
        setAlert({ severity: 'error', message: err.response.data.message });
      } else if (err.response?.data?.error) {
        setAlert({ severity: 'error', message: err.response.data.error });
      } else if (err.message) {
        setAlert({ severity: 'error', message: err.message });
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const isPromptEngineerInstruction = selectedInstruction?.sourceType === 'ai_prompt_engineer';
  const canReopenPromptEngineer = isPromptEngineerInstruction && Boolean(selectedInstruction?.promptEngineerConfig);

  const renderCreationChooser = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        How would you like to create your instructions?
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(320px, 420px))'
          },
          justifyContent: 'center',
          gap: 2,
          alignItems: 'stretch',
          width: '100%'
        }}
      >
        <Card
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minWidth: 0
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <SmartToyIcon color="primary" />
              <Typography variant="h6">Meet Your AI Instruction Architect</Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minHeight: { xs: 'auto', md: '3em' },
                lineHeight: 1.5
              }}
            >
              Answer a few business questions and the AI Instruction Architect will engineer
              production-ready instructions for you.
            </Typography>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
            <Button
              variant="contained"
              onClick={() => setCreationMode('promptEngineer')}
            >
              Start Your Interview
            </Button>
          </CardActions>
        </Card>

        {/*
        <Card
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minWidth: 0
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6">Use existing templates</Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minHeight: { xs: 'auto', md: '3em' },
                lineHeight: 1.5
              }}
            >
              Browse curated starter instructions and customize them to fit your
              needs.
            </Typography>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
            <Button
              variant="contained"
              onClick={() => setCreationMode('template')}
            >
              Browse Templates
            </Button>
          </CardActions>
        </Card>
        */}

        <Card
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minWidth: 0
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <ContentPasteGoIcon color="primary" />
              <Typography variant="h6">I already have instructions</Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                minHeight: { xs: 'auto', md: '3em' },
                lineHeight: 1.5
              }}
            >
              Paste your existing instructions or write them in your own words.
            </Typography>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
            <Button
              variant="contained"
              onClick={() => setCreationMode('paste')}
            >
              Paste Instructions
            </Button>
          </CardActions>
        </Card>
      </Box>
    </Box>
  );

  const renderTemplateSelection = () => {
    if (selectedTemplateKey) {
      return (
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Customize Template
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={resetTemplateSelection}
              variant="text"
            >
              Back to templates
            </Button>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="Instruction Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Instruction Content"
              value={templateContent}
              onChange={(e) => setTemplateContent(e.target.value)}
              minRows={12}
              multiline
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSaveFromTemplate}
                disabled={saving || !templateName.trim() || !templateContent.trim()}
              >
                Save Instruction
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCreationMode(null)}
                disabled={saving}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Select a template to start from</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setCreationMode(null)}
            variant="text"
          >
            Back
          </Button>
        </Stack>
        <Stack spacing={1}>
          {templateEntries.map(([key, content]) => (
            <Accordion
              elevation={1}
              key={key}
              expanded={expandedTemplateKey === key}
              onChange={() => setExpandedTemplateKey(expandedTemplateKey === key ? null : key)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{formatTemplateTitle(key)}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                    {content}
                  </Typography>
                  <Button variant="contained" onClick={() => handleTemplateSelect(key)}>
                    Use Template
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderManualEntry = () => (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Paste instructions</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setCreationMode(null)}
          variant="text"
        >
          Back
        </Button>
      </Stack>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Stack spacing={2}>
            <TextField
              label="Instruction Name"
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Instruction Content"
              value={manualContent}
              onChange={(e) => setManualContent(e.target.value)}
              minRows={16}
              multiline
              fullWidth
              placeholder="Paste your instructions here…"
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleSaveManual}
                disabled={saving || !manualName.trim() || !manualContent.trim()}
              >
                Save Instruction
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCreationMode(null)}
                disabled={saving}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', lg: 360 },
            flexShrink: 0,
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            p: 2
          }}
        >
          <InstructionChecker
            content={manualContent}
            onTemplateInsert={(template) => {
              setManualContent(template);
              if (!manualName?.trim()) {
                setManualName('Imported Instruction');
              }
            }}
            width="100%"
          />
        </Box>
      </Box>
    </Box>
  );

  const renderCreateContent = () => {
    switch (creationMode) {
      case 'template':
        return renderTemplateSelection();
      case 'wizard':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Instruction Builder Wizard</Typography>
              <Button startIcon={<ArrowBackIcon />} variant="text" onClick={() => setCreationMode(null)}>
                Back
              </Button>
            </Stack>
            <Divider />
            <InstructionBuilder onComplete={handleSaveInstruction} isSaving={saving} />
          </Box>
        );
      case 'paste':
        return renderManualEntry();
      case 'promptEngineer':
        return (
          <PromptEngineerFlow
            onExit={() => setCreationMode(null)}
            onSaveInstruction={handleSaveInstruction}
            onSaveForLater={handleSavePromptEngineerDraft}
            isSaving={saving}
            flowContext="instructions"
            onUnsavedChange={setPromptEngineerHasUnsaved}
            onSaveForLaterRequestChange={handlePromptEngineerSaveForLaterRequestChange}
            showInlineSaveForLaterAction={false}
          />
        );
      default:
        return renderCreationChooser();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {alert && (
        <SystemAlert open={alert !== null} severity={alert.severity} message={alert.message} />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" component="h1">
          {instructionId ? 'Edit Instructions' : 'Create Instructions'}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
          {(creationMode === 'promptEngineer' || editMode === 'promptEngineer') && promptEngineerSaveForLaterAction && (
            <Button
              variant="outlined"
              onClick={() => promptEngineerSaveForLaterAction({ leaveAfterSave: false })}
              disabled={saving}
            >
              Save For Later
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
          >
            Back to List
          </Button>
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 2, minWidth: 0 }}>
          {instructionId ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                {isPromptEngineerInstruction && (
                  <Chip label="Engineered By AI Instruction Architect" color="primary" size="small" />
                )}
                {selectedInstruction?.lifecycleState && (
                  <Chip
                    {...getInstructionLifecycleChipProps(selectedInstruction.lifecycleState)}
                    size="small"
                  />
                )}
                {canReopenPromptEngineer && editMode === 'standard' && (
                  <Button
                    variant="outlined"
                    startIcon={<SmartToyIcon />}
                    onClick={() => setConfirmReopenPromptEngineerOpen(true)}
                  >
                    Edit Interview Answers
                  </Button>
                )}
                {editMode === 'promptEngineer' && (
                  <Button
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => requestLeavePromptEngineer(() => setEditMode('standard'))}
                  >
                    Back to standard editor
                  </Button>
                )}
              </Stack>

              {editMode === 'promptEngineer' && canReopenPromptEngineer ? (
                <PromptEngineerFlow
                  onExit={() => setEditMode('standard')}
                  onSaveInstruction={handleSavePromptEngineerEdit}
                  onSaveForLater={handleSavePromptEngineerDraft}
                  isSaving={saving}
                  initialConfig={selectedInstruction?.promptEngineerConfig}
                  initialInstructionName={selectedInstruction?.name || ''}
                  initialDraftText={selectedInstruction?.content || ''}
                  resumeSnapshot={buildPromptEngineerResumeSnapshot(selectedInstruction)}
                  existingInstructionId={selectedInstruction?.id || null}
                  reopenMode={true}
                  flowContext={(selectedInstruction?.lifecycleState || '').toUpperCase() === 'DRAFT' ? 'instructions' : 'instructions'}
                  onUnsavedChange={setPromptEngineerHasUnsaved}
                  onSaveForLaterRequestChange={handlePromptEngineerSaveForLaterRequestChange}
                  showInlineSaveForLaterAction={false}
                />
              ) : (
                <AgentInstructions
                  selectedInstructionId={selectedInstructionId}
                  setSelectedInstructionId={setSelectedInstructionId}
                  onClose={handleCancel}
                  showActions={true}
                  hideSelect={false}
                  showPromptEngineerHeaderAction={false}
                />
              )}
            </Stack>
          ) : (
            renderCreateContent()
          )}
        </Box>
      </Box>

      <Dialog
        open={confirmReopenPromptEngineerOpen}
        onClose={() => setConfirmReopenPromptEngineerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reopen AI Instruction Architect?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Reopening the AI Instruction Architect uses your original interview responses. Manual changes made directly to the instructions will not be preserved unless you modify the interview answers accordingly.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReopenPromptEngineerOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmReopenPromptEngineerOpen(false);
              setEditMode('promptEngineer');
            }}
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmLeavePromptEngineerOpen}
        onClose={() => {
          setConfirmLeavePromptEngineerOpen(false);
          setPendingPromptEngineerDiscardAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Discard AI Instruction Architect Session?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your interview responses have not been saved. Leaving now will permanently discard your progress. This cannot be recovered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                const continueAction = pendingPromptEngineerDiscardAction;
                await promptEngineerSaveForLaterAction?.({ leaveAfterSave: false, leaveAction: continueAction });
                setConfirmLeavePromptEngineerOpen(false);
                setPendingPromptEngineerDiscardAction(null);
              } catch (err) {
                // Preserve workspace on save failure.
              }
            }}
            variant="contained"
            disabled={!promptEngineerSaveForLaterAction || saving}
          >
            Save and Leave
          </Button>
          <Button
            onClick={() => {
              setConfirmLeavePromptEngineerOpen(false);
              setPendingPromptEngineerDiscardAction(null);
            }}
            variant="outlined"
          >
            Continue Editing
          </Button>
          <Button
            onClick={() => {
              const continueAction = pendingPromptEngineerDiscardAction;
              setConfirmLeavePromptEngineerOpen(false);
              setPendingPromptEngineerDiscardAction(null);
              setPromptEngineerHasUnsaved(false);
              continueAction?.();
            }}
            color="error"
            variant="contained"
          >
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
